"use client";

// @motion/ui-theme — the motion vocabulary for Motion UI sections.
// One file sets the feel of an entire site; sections resolve tokens by name.

import { createGeneratorEasing, generateLinearEasing, spring } from "motion";
import { useReducedMotion } from "motion/react";
import { createContext, createElement, useContext, useMemo } from "react";
import type { ReactNode } from "react";

/** A cubic-bezier control-point tuple. */
export type CubicBezier = readonly [number, number, number, number];

/**
 * A named transition. One token carries both channels of a state change:
 *
 * - The physics half (`stiffness` + `damping`) drives travel — transforms,
 *   layout morphs, anything that moves. It carries velocity across
 *   interruptions.
 * - The `ease` + `duration` half is the matched channel for fades
 *   (opacity, colour), so both channels land together.
 */
export interface TransitionToken {
  type: "spring";
  /** Spring stiffness. */
  stiffness: number;
  /** Spring damping. */
  damping: number;
  /** Companion fade/colour duration in seconds. */
  duration: number;
  /** Companion curve for fades (opacity/colour), over `duration`. */
  ease: CubicBezier;
}

/** The five feel-vocabulary transitions every section resolves by name. */
export interface TransitionTokens {
  /** Instant feedback: toggles, tabs, hovers. */
  snap: TransitionToken;
  /** Default: menus, cards, reveals. */
  ui: TransitionToken;
  /** Large surfaces: sections, sheets, curtains. */
  gentle: TransitionToken;
  /** Celebratory: confetti, badges, counters. */
  lively: TransitionToken;
  /** Continuous background motion: pulses, sweeps, blinks. */
  ambient: TransitionToken;
}

export type TransitionName = keyof TransitionTokens;

/**
 * The resolved transition returned by `useMotionUITransition`: the token plus a
 * baked per-value `opacity` tween so fades ride along with the spring and land
 * together. Its ease is always `"linear"` — opacity is perceptually
 * compressed, so the token's decelerating curve front-loads visibility and
 * reads as a pop. `inherit: true` lets consumer-added top-level keys
 * (`repeat`, `delay`, `times`, ...) still reach the opacity channel.
 */
export interface UITransition extends TransitionToken {
  opacity: {
    type: "tween";
    duration: number;
    ease: "linear";
    inherit: true;
  };
}

/** Orchestration: stagger between children, in seconds. */
export interface StaggerTokens {
  tight: number;
  base: number;
  relaxed: number;
}

export type StaggerName = keyof StaggerTokens;

/** How far things travel on enter/hover, in pixels. */
export interface TravelTokens {
  hover: number;
  enter: number;
  section: number;
}

export type TravelName = keyof TravelTokens;

/** Viewport-entry defaults for `whileInView` sections. */
export interface InViewTokens {
  /** Fraction of the element that must be visible before it animates in. */
  amount: number;
  /** Animate only the first time it enters the viewport. */
  once: boolean;
}

/**
 * How to degrade when the user asks for reduced motion.
 * - `"calm"`: kill travel, keep opacity fades (duration-based).
 * - `"off"`: no animation at all.
 */
export type ReducedMotionStrategy = "calm" | "off";

/** The full, resolved theme. `defineTheme` always returns this complete shape. */
export interface MotionUITheme {
  transitions: TransitionTokens;
  stagger: StaggerTokens;
  travel: TravelTokens;
  inView: InViewTokens;
  reducedMotion: ReducedMotionStrategy;
}

/** Recursive partial: any subtree of a theme may be omitted in a config. */
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends readonly unknown[]
    ? T[K]
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

export type MotionUIThemeConfig = DeepPartial<MotionUITheme>;

function transition(
  duration: number,
  stiffness: number,
  damping: number,
  ease: CubicBezier,
): TransitionToken {
  return { type: "spring", duration, stiffness, damping, ease };
}

const defaultOut: CubicBezier = [0.22, 1, 0.36, 1];
const defaultInOut: CubicBezier = [0.65, 0, 0.35, 1];

/** The bundled default theme, used when no provider is mounted. */
export const defaultTheme: MotionUITheme = {
  transitions: {
    snap: transition(0.15, 1218.4696791468346, 69.81317007977319, defaultOut),
    ui: transition(0.3, 304.61741978670864, 33.16125578789226, defaultOut),
    gentle: transition(0.5, 109.6622711232151, 19.896753472735355, defaultOut),
    lively: transition(0.21, 621.668203646344, 17.453292519943293, defaultOut),
    ambient: transition(0.8, 42.8368246575059, 13.089969389957473, defaultInOut),
  },
  stagger: { tight: 0.04, base: 0.08, relaxed: 0.15 },
  travel: { hover: 4, enter: 24, section: 48 },
  inView: { amount: 0.4, once: true },
  reducedMotion: "calm",
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Deep-merge a partial config over a complete base. Plain objects merge
 * recursively; arrays (easing tuples) and primitives replace wholesale.
 */
function deepMerge<T>(base: T, override: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(override)) return override as T;

  const result: Record<string, unknown> = { ...base };
  for (const key of Object.keys(override)) {
    const overrideValue = override[key];
    if (overrideValue === undefined) continue;
    result[key] = deepMerge(
      (base as Record<string, unknown>)[key],
      overrideValue,
    );
  }
  return result as T;
}

/**
 * Build a complete, resolved `MotionUITheme` from a partial config by
 * deep-merging over the bundled `defaultTheme`.
 */
export function defineTheme(config: MotionUIThemeConfig = {}): MotionUITheme {
  // structuredClone so the resolved theme never aliases defaultTheme's
  // sub-objects: deepMerge only recurses into overridden branches.
  return structuredClone(deepMerge(defaultTheme, config));
}

/** Runtime motion posture resolved for the current visitor. */
export type MotionMode = "full" | ReducedMotionStrategy;

/** The resolved motion posture a section should render with. */
export interface ResolvedReducedMotion {
  strategy: MotionMode;
  animate: boolean;
  travel: boolean;
  opacityOnly: boolean;
}

/**
 * Resolve how a section should behave given the theme's reduced-motion
 * strategy and whether the user currently prefers reduced motion.
 */
export function resolveReducedMotion(
  theme: MotionUITheme,
  prefersReducedMotion: boolean,
): ResolvedReducedMotion {
  if (!prefersReducedMotion) {
    return { strategy: "full", animate: true, travel: true, opacityOnly: false };
  }
  if (theme.reducedMotion === "off") {
    return { strategy: "off", animate: false, travel: false, opacityOnly: false };
  }
  return { strategy: "calm", animate: true, travel: false, opacityOnly: true };
}

/** Format a number without a trailing `.0` / exponent, for CSS output. */
function num(value: number): string {
  return Number(value.toFixed(4)).toString();
}

function cubicBezier(points: CubicBezier): string {
  return `cubic-bezier(${points.map(num).join(", ")})`;
}

/**
 * Compile a transition token's spring channel to a CSS `linear()` easing
 * string plus its natural settle duration in seconds.
 */
export function transitionToLinear(token: TransitionToken): {
  easing: string;
  duration: number;
} {
  const easing = createGeneratorEasing(
    { stiffness: token.stiffness, damping: token.damping },
    100,
    spring,
  );
  return {
    // generateLinearEasing takes duration in milliseconds.
    easing: generateLinearEasing(easing.ease, easing.duration * 1000),
    duration: easing.duration,
  };
}

/** Emit the theme as a flat record of `--motion-ui-*` custom properties. */
export function themeToCssVars(theme: MotionUITheme): Record<string, string> {
  const vars: Record<string, string> = {};

  const transitionNames = Object.keys(theme.transitions) as TransitionName[];
  for (const name of transitionNames) {
    const token = theme.transitions[name];
    vars[`--motion-ui-transition-${name}`] = cubicBezier(token.ease);
    vars[`--motion-ui-transition-${name}-duration`] = `${num(token.duration)}s`;
    const { easing, duration } = transitionToLinear(token);
    vars[`--motion-ui-transition-${name}-spring`] = easing;
    vars[`--motion-ui-transition-${name}-spring-duration`] = `${num(duration)}s`;
  }

  for (const [name, value] of Object.entries(theme.stagger)) {
    vars[`--motion-ui-stagger-${name}`] = `${num(value)}s`;
  }

  for (const [name, value] of Object.entries(theme.travel)) {
    vars[`--motion-ui-travel-${name}`] = `${num(value)}px`;
  }

  return vars;
}

/** Render the theme's custom properties as a `:root { ... }` CSS block. */
export function cssVarsToStyleString(theme: MotionUITheme): string {
  const vars = themeToCssVars(theme);
  const body = Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join("\n");
  return `:root {\n${body}\n}`;
}

const UIThemeContext = createContext<MotionUITheme | null>(null);

export interface MotionUIThemeProviderProps {
  theme?: MotionUITheme;
  children: ReactNode;
}

/**
 * Provide a theme to every Motion UI section below it. Omitting `theme`
 * supplies the default, which is also what `useMotionUITheme` returns with no
 * provider present, so wrapping is optional.
 */
export function MotionUIThemeProvider({
  theme,
  children,
}: MotionUIThemeProviderProps) {
  const value = theme ?? defaultTheme;
  return createElement(UIThemeContext.Provider, { value }, children);
}

function useThemeContext(): MotionUITheme {
  return useContext(UIThemeContext) ?? defaultTheme;
}

/** The active theme plus the resolved runtime motion posture. */
export interface ResolvedMotionUITheme extends MotionUITheme {
  motionMode: MotionMode;
}

/**
 * Read the active Motion UI theme and its resolved runtime motion posture.
 * Returns `defaultTheme` when no provider is mounted.
 */
export function useMotionUITheme(): ResolvedMotionUITheme {
  const theme = useThemeContext();
  const prefersReducedMotion = !!useReducedMotion();
  const motionMode = resolveReducedMotion(theme, prefersReducedMotion).strategy;

  return useMemo(() => ({ ...theme, motionMode }), [theme, motionMode]);
}

/**
 * Resolve a named transition to a Motion-compatible transition object, ready
 * to spread into `transition` on a `motion` component.
 */
export function useMotionUITransition(name: TransitionName): UITransition {
  const theme = useThemeContext();
  const token = theme.transitions[name];
  return useMemo(() => {
    const { duration, stiffness, damping, ease } = token;
    return {
      type: "spring",
      stiffness,
      damping,
      duration,
      ease,
      // inherit: true lets consumer-added top-level keys (repeat, delay,
      // times, ...) reach the opacity channel.
      opacity: { type: "tween", duration, ease: "linear", inherit: true },
    };
  }, [token]);
}
